import React from 'react';
import Link from 'next/link';
import styled, { css } from 'styled-components';

import useTranslation from 'hooks/use-translation';
import usePaths from 'src/hooks/use-paths';
import { InlineBanner } from 'src/components/inline-banner';
import { AnchorButton } from 'src/components/core/button';
import { ShieldCheck } from 'src/svg/shield-check';

export function VerifyPhoneToRedeemNotice(): JSX.Element {
  const { t } = useTranslation();
  const { href: baseHref } = usePaths();
  const accountHref = `${baseHref}/user/account`;

  const message = t('loyalty.verifyPhoneToRedeem.message', 'Verify your phone number to redeem rewards online.');
  const ctaLabel = t('loyalty.verifyPhoneToRedeem.cta', 'Verify now');

  return (
    <InlineBanner severity='warning' border icon={<ShieldCheck />} contentStyles={contentStyles}>
      <Row>
        <Message>{message}</Message>
        <ButtonWrapper>
          <AnchorButton as={Link} href={accountHref} variant='outlinePrimary' size='small'>
            {ctaLabel}
          </AnchorButton>
        </ButtonWrapper>
      </Row>
    </InlineBanner>
  );
}

const contentStyles = css`
  max-width: 100%;
`;

const Row = styled.div`
  align-items: center;
  display: flex;
  flex-wrap: wrap;
  column-gap: 16px;
  row-gap: 16px;
  justify-content: space-between;
  width: 100%;
`;

const Message = styled.span`
  flex: 1 1 auto;
  min-width: 0;
`;

const ButtonWrapper = styled.div`
  flex-shrink: 0;
`;

/**
 * Shared wrapper for a widget (CashRedemption/DiscountRedemption/LoyaltyTiers) and its sibling
 * VerifyPhoneToRedeemNotice — keeps the widget's own margin/max-width so the two visually group
 * together, while rendering the notice as a sibling (not nested inside the widget's own anchor) to
 * avoid nested <a> tags when the widget itself is a link (ECOMM-8974).
 */
export const WidgetGroup = styled.div<{ $locationMargin?: string }>`
  display: flex;
  flex-direction: column;
  gap: 12px;
  margin: ${({ $locationMargin }) => $locationMargin || '20px auto 40px'};
  max-width: 1200px;
  width: 100%;
`;
